import React, { useState, useContext } from 'react';
import axios from 'axios';
import { Card } from '../design-system';
import { useToggle } from 'react-use';
import Alert from '../helpers/alert';
import { successRefetch } from '../helpers/data-loader';
import {
  required,
  createSelectOptions,
  catchPlanDetailError,
} from '../helpers/utils';
import ModalForm from './ModalForm';
import { CollapsibleRowTable, TableBody, TableCardHeader } from './Table';
import {
  ACTIONS_PRIMARY_SECONDARY_COLUMNS,
  ACTION_TYPES,
  ACTION_STATUSES,
  NOTES_FIELD,
  ERR_MSG,
} from '../globalConstants';
import { Session } from '../contexts/sessionContext';

interface ActionsProps {
  data: any[];
  modelId: any;
  modelType: string;
  refetch: () => void;
  maxHeight?: any;
  canEdit: boolean;
  handleDeleteNote: (id: any) => void;
  handleUpdateNote: (values: any) => void;
}

const Actions = ({
  data,
  modelId,
  modelType,
  refetch,
  maxHeight,
  canEdit,
  handleDeleteNote,
  handleUpdateNote,
}: ActionsProps) => {
  const {
    state: {
      user: { members },
    },
  } = useContext(Session);
  const [targetedAction, setTargetedAction] = useState<any>('');
  const [isOpenAddAction, toggleAdd] = useToggle(false);
  const [isOpenEditAction, toggleEdit] = useToggle(false);
  const [isOpenAddNote, toggleAddNote] = useToggle(false);
  const [isOpenNotifyAction, toggleNotifyAction] = useToggle(false);

  const NOTIFY_FIELDS = [
    {
      name: 'notification_target',
      label: 'Collaborator To Notify',
      type: 'dropdown',
      component: 'select',
      options: createSelectOptions(members),
    },
    {
      name: 'send_as_urgent',
      id: 'urgent',
      label: 'Notify As Urgent',
      type: 'checkbox',
      component: 'checkbox',
    },
    {
      name: 'message',
      label: 'Message Addendum',
      type: 'text',
      multiline: true,
    },
  ];
  const ACTION_FIELDS = [
    { name: 'title', label: 'Title', validate: required },
    {
      name: 'assignee_id',
      label: 'Assignee',
      component: 'select',
      options: createSelectOptions(members),
      validate: required,
    },
    {
      name: 'type',
      label: 'Type',
      component: 'select',
      validate: required,
      options: ACTION_TYPES,
    },
    {
      name: 'status',
      label: 'Status',
      component: 'select',
      options: ACTION_STATUSES,
    },
    {
      name: 'deadline',
      label: 'Deadline',
      type: 'date',
      max: '2100-01-01',
      min: '2000-01-01',
    },
    {
      id: 'actionHardDeadline',
      name: 'hard_deadline',
      label: 'Hard Deadline',
      component: 'checkbox',
      width: 150,
    },
    {
      id: 'urgent',
      name: 'urgent',
      label: 'Urgent',
      component: 'checkbox',
      width: 100,
    },
    {
      id: 'required',
      name: 'required',
      label: 'Funding Requirement',
      component: 'checkbox',
      width: 250,
    },
  ];

  const openEditModal = async (values) => {
    setTargetedAction(values);
    toggleEdit(true);
  };

  const openAddActionNoteModal = async (values) => {
    setTargetedAction(values);
    toggleAddNote(true);
  };

  const openNotifyAction = async (values) => {
    setTargetedAction(values);
    toggleNotifyAction(true);
  };
  const onSubmit = async (values) => {
    const payload = {
      ...values,
      modelId,
      modelType,
      hard_deadline: values.hard_deadline && 1,
      urgent: values.urgent && 1,
      required: values.required && 1,
    };
    try {
      await axios.post('/action', payload);
      successRefetch(refetch);
      toggleAdd();
    } catch (error) {
      catchPlanDetailError(error?.response);
    }
  };

  const handleUpdateAction = async (values) => {
    setTargetedAction(values);
    try {
      await axios.put(`/action/${values.id}`, values);
      successRefetch(refetch);
      toggleEdit();
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  const handleNotifyAction = async (values) => {
    const payload = {
      ...values,
      notification_target: values.notification_target || values.assignee_id,
    };
    setTargetedAction(payload);
    try {
      await axios.post(`/notify`, payload);
      successRefetch(refetch, 'Email Sent Succesfully!');
      toggleNotifyAction();
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  const handleDeleteAction = async (action_id) => {
    if (confirm('Are you sure?') == true) {
      try {
        await axios.delete(`/action/${action_id}`);
        successRefetch(refetch);
      } catch (error) {
        Alert.error(ERR_MSG);
      }
    }
  };

  const handleAddNoteToAction = async (values) => {
    setTargetedAction(values);
    const payload = {
      ...values,
      modelId,
      modelType,
      action_id: targetedAction.id,
    };
    try {
      await axios.post(`/note`, payload);
      toggleAddNote();
      successRefetch(refetch);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  return (
    <Card mt={20}>
      <TableCardHeader
        name="Actions"
        action="Add Action"
        onClick={toggleAdd}
        disabled={!canEdit}
      />
      <TableBody {...({ maxHeight } as any)}>
        <CollapsibleRowTable
          name="Actions"
          columns={ACTIONS_PRIMARY_SECONDARY_COLUMNS}
          data={data.map((item) => ({
            ...item,
            handleUpdateAction,
            handleDeleteNote,
            openAddActionNoteModal,
            handleDeleteAction,
            handleUpdateNote,
            canEdit,
            openEditModal,
            openNotifyAction,
            handleNotifyAction,
          }))}
        />
      </TableBody>
      <ModalForm
        title="Add Action"
        isOpen={isOpenAddAction}
        onSubmit={onSubmit}
        onClose={toggleAdd}
        fields={[
          ...ACTION_FIELDS,
          { name: 'note', label: 'Note', multiline: true },
        ]}
        oneColumn
      />
      <ModalForm
        title="Edit Action"
        isOpen={isOpenEditAction}
        onSubmit={handleUpdateAction}
        onClose={toggleEdit}
        fields={[...ACTION_FIELDS]}
        oneColumn
        initialValues={targetedAction}
      />
      <ModalForm
        title="Notify Collaborator"
        isOpen={isOpenNotifyAction}
        onSubmit={handleNotifyAction}
        onClose={toggleNotifyAction}
        fields={[...NOTIFY_FIELDS]}
        initialValues={targetedAction}
        buttonText="Send"
        oneColumn
      />
      {targetedAction && (
        <ModalForm
          title={`Add Note`}
          isOpen={isOpenAddNote}
          onSubmit={handleAddNoteToAction}
          onClose={toggleAddNote}
          fields={NOTES_FIELD}
        />
      )}
    </Card>
  );
};

export default Actions;
